// §J (Addendum June 2026): GBS GA Window — admin-configurable thresholds.
// Stored at settings/gbsConfig in Firestore. Changes take effect immediately — no code deploy needed.

import {
  Box,
  Divider,
  Flex,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { DEFAULT_GBS_CONFIG, GBS_CONFIG_DOC, GbsConfig, SETTINGS } from '@hb/shared'
import { doc, setDoc } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { db } from '../../../../backend/db/db'
import { useDocument } from '../../../../backend/db/hooks'
import { SolidActionButton } from '../../../Buttons'

const formatGaLabel = (days: number) => `${Math.floor(days / 7)}w ${days % 7}d (${days}d)`

const useGbsConfigAdmin = () => {
  const { data, loading } = useDocument<GbsConfig>(SETTINGS, GBS_CONFIG_DOC)

  const merged: GbsConfig = useMemo(() => ({ ...DEFAULT_GBS_CONFIG, ...data }), [data])

  const saveConfig = useCallback(
    async (updates: Partial<GbsConfig>): Promise<{ success?: string; error?: string }> => {
      const docRef = doc(db, SETTINGS, GBS_CONFIG_DOC)
      try {
        await setDoc(docRef, { ...merged, ...updates }, { merge: true })
        return { success: 'Saved' }
      } catch (e) {
        return { error: (e as Error).message }
      }
    },
    [merged],
  )

  return { config: merged, loading, saveConfig }
}

export const GbsConfigAdminView = () => {
  const { config, loading, saveConfig } = useGbsConfigAdmin()

  const [triggerDays, setTriggerDays] = useState<string>(String(config.gbsGaTriggerDays))
  const [windowEndDays, setWindowEndDays] = useState<string>(String(config.gbsWindowEndDays))
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  if (loading) {
    return (
      <Flex p={8} justify="center">
        <Spinner />
      </Flex>
    )
  }

  const handleSave = async () => {
    setSaving(true)
    setMsg('')
    const trigger = Number(triggerDays)
    const end = Number(windowEndDays)
    if (isNaN(trigger) || isNaN(end) || trigger < 1 || end < trigger) {
      setMsg('Invalid values — trigger must be less than window end')
      setSaving(false)
      return
    }
    const result = await saveConfig({
      gbsGaTriggerDays: trigger,
      gbsWindowEndDays: end,
      gbsPassedDays: end + 1,
    })
    setMsg(result.success ?? result.error ?? '')
    setSaving(false)
  }

  return (
    <VStack align="stretch" spacing={3} p={4} h="100%" overflowY="auto">
      <Box>
        <Text fontFamily="Hero-New" fontWeight={700} fontSize="md">
          GBS Collection Window Thresholds
        </Text>
        <Text fontSize="sm" color="gray.500" mt={1}>
          Controls when the GBS culture field appears and when the Tier 1 collection window fires.
          Default: 35–37w (245–265 days). Changes take effect immediately — no code deploy required.
        </Text>
      </Box>
      <Divider />

      <VStack align="stretch" spacing={4} maxW="400px">
        <Box>
          <Text
            fontSize="sm"
            fontWeight={600}
            color="gray.700"
            _dark={{ color: 'gray.200' }}
            mb={1}>
            Collection window start (trigger)
          </Text>
          <Text fontSize="xs" color="gray.500" mb={2}>
            GBS field appears and becomes Tier 1 at this GA. Default: 245 days (35w0d).
          </Text>
          <HStack>
            <NumberInput
              value={triggerDays}
              onChange={setTriggerDays}
              min={1}
              max={280}
              size="sm"
              w="100px">
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Text fontSize="xs" color="gray.500">
              days — {formatGaLabel(Number(triggerDays) || config.gbsGaTriggerDays)}
            </Text>
          </HStack>
        </Box>

        <Box>
          <Text
            fontSize="sm"
            fontWeight={600}
            color="gray.700"
            _dark={{ color: 'gray.200' }}
            mb={1}>
            Collection window end
          </Text>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Last day (inclusive) the GBS field is Tier 1. Default: 265 days (37w+6d). The "window
            passed" flag fires the following day ({Number(windowEndDays) + 1}d).
          </Text>
          <HStack>
            <NumberInput
              value={windowEndDays}
              onChange={setWindowEndDays}
              min={1}
              max={300}
              size="sm"
              w="100px">
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Text fontSize="xs" color="gray.500">
              days — {formatGaLabel(Number(windowEndDays) || config.gbsWindowEndDays)}
            </Text>
          </HStack>
        </Box>

        <Flex align="center" gap={3} pt={1}>
          <SolidActionButton size="sm" onClick={handleSave} isLoading={saving}>
            Save
          </SolidActionButton>
          {msg && (
            <Text fontSize="xs" color={msg.startsWith('Saved') ? 'green.500' : 'red.500'}>
              {msg}
            </Text>
          )}
        </Flex>
      </VStack>
    </VStack>
  )
}
